import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import { listenerLevels, currentListenerProfile } from '@/mocks/listenerLevels';

export default function Niveles() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);

  const profile = currentListenerProfile;
  const currentLevelData = listenerLevels.find((l) => l.level === profile.currentLevel)!;
  const nextLevelData = listenerLevels.find((l) => l.level === profile.currentLevel + 1)!;

  const toggleExpand = (level: number) => {
    setExpandedLevel(expandedLevel === level ? null : level);
  };

  return (
    <div className="min-h-screen bg-background-100/50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-10">

          {/* Hero — Current Level */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-52 md:h-64 bg-gradient-to-br from-orange-500/25 via-amber-400/20 to-rose-400/10 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/25"></div>

              {/* Decorative circles */}
              <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-orange-500/10 blur-3xl"></div>
              <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-amber-400/10 blur-3xl"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
                <img
                  src={profile.photo}
                  alt={profile.name}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover object-top border-4 border-background-50 shadow-sm mb-4"
                />
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-1">{profile.name}</h2>
                <p className="text-xs text-foreground-500 mb-4">{profile.username}</p>

                {/* Current Level Badge */}
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${currentLevelData.colorLight} mb-4`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${currentLevelData.color}`}>
                    <i className={`${currentLevelData.icon} text-background-50`}></i>
                  </div>
                  <div className="text-left">
                    <div className={`text-xs font-bold ${currentLevelData.textColor}`}>Nivel {currentLevelData.level}</div>
                    <div className={`text-sm font-black ${currentLevelData.textColor}`}>{currentLevelData.name}</div>
                  </div>
                </div>

                {/* Progress to next */}
                <div className="w-full max-w-md">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-foreground-500">
                      {profile.currentLevelName}
                    </span>
                    <span className="text-xs font-bold text-foreground-700">
                      {profile.pointsToNextLevel - profile.currentPoints} pts para {profile.nextLevelName}
                    </span>
                  </div>
                  <div className="w-full h-3 bg-background-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-orange-400 to-amber-500 transition-all duration-1000 ease-out"
                      style={{ width: `${profile.progressPercent}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[11px] text-foreground-400">{profile.currentPoints.toLocaleString()} pts</span>
                    <span className="text-[11px] text-foreground-400">{profile.pointsToNextLevel.toLocaleString()} pts</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 divide-x divide-background-200">
              {[
                { label: 'Oyentes en tu nivel', value: profile.totalListenersAtLevel.toLocaleString(), icon: 'ri-group-line' },
                { label: 'Ranking Global', value: `#${profile.globalRank.toLocaleString()}`, icon: 'ri-medal-line' },
                { label: 'Próximo Nivel', value: profile.nextLevelName, icon: 'ri-arrow-up-line' },
              ].map((stat) => (
                <div key={stat.label} className="px-4 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} text-foreground-400 text-xs`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-sm font-bold text-foreground-950">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* All Levels Grid */}
          <div className="mt-8">
            <div className="flex items-center gap-3 mb-5">
              <h3 className="text-sm font-bold text-foreground-950">Sistema de Niveles RFW</h3>
              <span className="w-px h-4 bg-background-300"></span>
              <span className="text-xs text-foreground-500">{listenerLevels.length} niveles</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listenerLevels.map((level) => {
                const isCurrent = level.level === profile.currentLevel;
                const isUnlocked = profile.currentLevel >= level.level;
                const isNext = level.level === profile.currentLevel + 1;

                return (
                  <div
                    key={level.level}
                    className={`bg-background-50 rounded-2xl overflow-hidden transition-all ${
                      isCurrent ? 'ring-2 ring-orange-400 shadow-sm' : ''
                    } ${!isUnlocked ? 'opacity-60' : ''}`}
                  >
                    {/* Card Header */}
                    <div className={`p-5 ${isCurrent ? currentLevelData.colorLight : 'bg-background-100'}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isUnlocked ? level.color : 'bg-foreground-300'}`}>
                          <i className={`${level.icon} ${isUnlocked ? 'text-background-50' : 'text-background-100'} text-lg`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-bold text-foreground-400">Nivel {level.level}</span>
                            {isCurrent && (
                              <span className="px-2 py-0.5 bg-orange-500 text-background-50 text-[10px] font-bold rounded-full whitespace-nowrap">
                                Actual
                              </span>
                            )}
                            {isNext && (
                              <span className="px-2 py-0.5 bg-amber-500 text-background-50 text-[10px] font-bold rounded-full whitespace-nowrap">
                                Próximo
                              </span>
                            )}
                            {!isUnlocked && !isNext && (
                              <span className="px-2 py-0.5 bg-background-200 text-foreground-500 text-[10px] font-bold rounded-full whitespace-nowrap">
                                Bloqueado
                              </span>
                            )}
                          </div>
                          <h4 className={`text-base font-black ${isUnlocked ? 'text-foreground-950' : 'text-foreground-500'}`}>
                            {level.name}
                          </h4>
                          <p className="text-xs text-foreground-500 mt-0.5">{level.subtitle}</p>
                        </div>
                      </div>
                    </div>

                    {/* Progress section for current level */}
                    {isCurrent && (
                      <div className="px-5 pt-4 pb-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-foreground-500">Progreso a {nextLevelData.name}</span>
                          <span className="text-[10px] font-bold text-foreground-700">{profile.progressPercent}%</span>
                        </div>
                        <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-orange-400 to-amber-500 rounded-full transition-all duration-1000 ease-out"
                            style={{ width: `${profile.progressPercent}%` }}
                          ></div>
                        </div>
                        <div className="text-[10px] text-foreground-400 mt-1">
                          {profile.currentPoints.toLocaleString()} / {profile.pointsToNextLevel.toLocaleString()} pts
                        </div>
                      </div>
                    )}

                    {/* Requirements */}
                    <div className="px-5 py-3 border-t border-background-100">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-foreground-600">
                          {isUnlocked ? 'Desbloqueado' : 'Requisito'}
                        </span>
                        <span className={`text-xs font-bold ${isUnlocked ? level.textColor : 'text-foreground-400'}`}>
                          {level.unlockedAt}
                        </span>
                      </div>
                    </div>

                    {/* Benefits */}
                    <div className="px-5 pb-2">
                      <button
                        onClick={() => toggleExpand(level.level)}
                        className="w-full flex items-center justify-between py-2 text-xs font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                      >
                        <span>Beneficios ({level.benefits.length})</span>
                        <i className={`text-xs transition-transform ${expandedLevel === level.level ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                      </button>
                      {(expandedLevel === level.level || isCurrent) && (
                        <div className="space-y-1.5 pb-3">
                          {level.benefits.map((benefit, idx) => (
                            <div
                              key={idx}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                                isUnlocked ? 'bg-background-100 text-foreground-700' : 'bg-background-100/50 text-foreground-400'
                              }`}
                            >
                              <i className={`${isUnlocked ? 'ri-check-line text-emerald-500' : 'ri-lock-line text-foreground-400'} text-xs`}></i>
                              {benefit}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Progress bar between levels (non-current, unlocked) */}
                    {isUnlocked && !isCurrent && (
                      <div className="px-5 pb-4">
                        <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${level.color}`} style={{ width: '100%' }}></div>
                        </div>
                      </div>
                    )}

                    {!isUnlocked && (
                      <div className="px-5 pb-4">
                        <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                          <div className="h-full bg-background-300 rounded-full" style={{ width: '0%' }}></div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* How it Works */}
          <div className="mt-8 bg-background-50 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-5">
              <i className="ri-question-line text-accent-500"></i>
              ¿Cómo funciona?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-primary-500 flex items-center justify-center">
                  <i className="ri-headphone-line text-background-50 text-xl"></i>
                </div>
                <h4 className="text-sm font-bold text-foreground-950 mb-1.5">1. Interactuá</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Escuchá radios, enviá saludos, solicitá canciones, participá en concursos y eventos.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-accent-500 flex items-center justify-center">
                  <i className="ri-flashlight-line text-background-50 text-xl"></i>
                </div>
                <h4 className="text-sm font-bold text-foreground-950 mb-1.5">2. Sumá Puntos</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Cada acción te da puntos. Cuanto más participás, más rápido subís de nivel.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-secondary-500 flex items-center justify-center">
                  <i className="ri-star-line text-background-50 text-xl"></i>
                </div>
                <h4 className="text-sm font-bold text-foreground-950 mb-1.5">3. Desbloqueá</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  Cada nivel desbloquea nuevos beneficios, insignias y experiencias exclusivas.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 bg-background-50 rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
              <i className="ri-rocket-line text-background-50 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950 mb-2">¿Listo para subir de nivel?</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Cada interacción te acerca más a ser Embajador RFW. Descargá la app y empezá a sumar puntos hoy.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/gamificacion"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flashlight-line mr-1.5"></i>
                Ver gamificación
              </Link>
              <Link
                to="/insignias"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-award-line mr-1.5"></i>
                Ver insignias
              </Link>
            </div>
          </div>

        </div>
      </main>

      <Footer />

      <div className="md:hidden h-20"></div>
    </div>
  );
}